import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Grid, Card, CardContent,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, FormControl, InputLabel, Select, MenuItem,
  Chip, CircularProgress, Alert, AlertTitle, Button, IconButton, Tooltip,
  Stack, Pagination, Divider, Dialog, DialogTitle, DialogContent,
  DialogActions, Menu, ListItemIcon, ListItemText, Badge, Tabs, Tab
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Warning as PenaltyIcon,
  MonetizationOn as MoneyIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  ErrorOutline as ErrorIcon,
  HourglassEmpty as PendingIcon,
  MoreVert as MoreVertIcon,
  OpenInNew as OpenInNewIcon,
  Send as ApplyIcon,
  Block as WaiveIcon,
  NoteAdd as NoteIcon,
  Paid as CollectedIcon,
  Person as PersonIcon,
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  History as EventIcon,
  Gavel as DisputeIcon,
  Assignment as ReservationIcon,
  TrendingDown as DeductionIcon,
  Schedule as ScheduledIcon,
  AccountBalanceWallet as VictimIcon,
  PriorityHigh as UrgentIcon,
  Handshake as CompensateIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useSnackbar } from 'notistack';
import penaltyService from '../../services/penaltyService';

// ─── Constants ─────────────────────────────────────────────────────────────

const PENALTY_STATUSES = [
  'draft', 'pending', 'scheduled', 'applied', 'failed',
  'disputed', 'waived', 'collected', 'cancelled', 'refunded', 'pending_collection'
];

const PENALTY_TYPES = [
  'no_show', 'late_return', 'damage', 'policy_violation', 'repeated_offense',
  'admin_decision', 'monetary', 'deposit_withhold', 'payout_reduction',
  'platform_fee', 'reputation', 'restriction', 'ban', 'warning', 'compliance_task'
];

const SOURCE_TYPES = ['dispute', 'fraud_case', 'kyc_case', 'manual', 'booking_event', 'other'];

const STATUS_CHIP = {
  draft:              { color: 'default',   label: 'Brouillon' },
  pending:            { color: 'warning',   label: 'En attente' },
  scheduled:          { color: 'info',      label: 'Planifié' },
  applied:            { color: 'success',   label: 'Appliqué' },
  pending_collection: { color: 'warning',   label: 'Recouvrement...' },
  failed:             { color: 'error',     label: 'Échec' },
  disputed:           { color: 'error',     label: 'Contesté' },
  waived:             { color: 'default',   label: 'Annulé' },
  collected:          { color: 'success',   label: 'Collecté' },
  cancelled:          { color: 'default',   label: 'Annulé' },
  refunded:           { color: 'info',      label: 'Remboursé' }
};

const TYPE_LABEL = {
  no_show: 'No-show', late_return: 'Retard', damage: 'Dommage',
  policy_violation: 'Violation politique', repeated_offense: 'Récidive',
  admin_decision: 'Décision admin', monetary: 'Monétaire',
  deposit_withhold: 'Retenue caution', payout_reduction: 'Réduction payout',
  platform_fee: 'Frais plateforme', reputation: 'Réputation',
  restriction: 'Restriction', ban: 'Bannissement', warning: 'Avertissement',
  compliance_task: 'Conformité'
};

const SOURCE_LABEL = {
  dispute: 'Litige', fraud_case: 'Fraude', kyc_case: 'KYC',
  manual: 'Manuel', booking_event: 'Réservation', other: 'Autre'
};

const formatDate = (d) => {
  if (!d) return '-';
  try { return format(new Date(d), 'dd/MM/yyyy HH:mm', { locale: fr }); }
  catch { return '-'; }
};

const formatAmount = (a, currency = 'EUR') =>
  a != null ? `${parseFloat(a).toFixed(2)} ${currency}` : '-';

// ─── Stat Card ──────────────────────────────────────────────────────────────

const StatCard = ({ title, value, color = 'primary', icon, sub }) => (
  <Card elevation={2} sx={{ height: '100%' }}>
    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Box sx={{
        bgcolor: `${color}.lighter`, color: `${color}.main`,
        borderRadius: 2, p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center'
      }}>
        {icon}
      </Box>
      <Box>
        <Typography variant="h4" fontWeight="bold" color={`${color}.main`}>
          {value ?? <CircularProgress size={20} />}
        </Typography>
        <Typography variant="body2" color="text.secondary">{title}</Typography>
        {sub && <Typography variant="caption" color="text.disabled">{sub}</Typography>}
      </Box>
    </CardContent>
  </Card>
);

// ─── Events Timeline ────────────────────────────────────────────────────────

const PenaltyTimeline = ({ events = [] }) => {
  if (!events.length) return <Typography variant="body2" color="text.secondary">Aucun événement.</Typography>;
  return (
    <Box>
      {[...events].reverse().map((ev, i) => (
        <Box key={ev.id || i} sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
          <Box sx={{ mt: 0.5 }}>
            <Chip size="small" label={ev.event_type} variant="outlined" sx={{ fontSize: '0.7rem' }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            {ev.message && <Typography variant="body2">{ev.message}</Typography>}
            <Typography variant="caption" color="text.secondary">
              {formatDate(ev.created_at)}{ev.actor?.name ? ` — ${ev.actor.name}` : ''}
            </Typography>
            {ev.metadata && Object.keys(ev.metadata).length > 0 && (
              <Box component="pre" sx={{ fontSize: '0.7rem', bgcolor: 'grey.50', p: 1, borderRadius: 1, mt: 0.5, overflowX: 'auto' }}>
                {JSON.stringify(ev.metadata, null, 2)}
              </Box>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

// ─── Victim Compensation Card ────────────────────────────────────────────────

const VICTIM_COMP_STATUS_CONFIG = {
  requires_manual_action: { color: 'error',   label: '🚨 Action manuelle requise' },
  transferred:            { color: 'success',  label: '✅ Virement Stripe effectué' },
  compensated:            { color: 'success',  label: '✅ Indemnisé manuellement' },
  manual:                 { color: 'warning',  label: '⏳ En attente (manuel)' },
};

const VictimCompensationCard = ({ victimComp }) => {
  if (!victimComp || !victimComp.status) return null;
  const cfg = VICTIM_COMP_STATUS_CONFIG[victimComp.status] || { color: 'default', label: victimComp.status };
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: `${cfg.color}.main`, borderWidth: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <VictimIcon color={cfg.color} />
        <Typography variant="subtitle2" color={`${cfg.color}.main`} fontWeight="bold">
          Compensation victime
        </Typography>
        <Chip size="small" label={cfg.label} color={cfg.color} />
      </Stack>
      <Stack spacing={0.5}>
        {victimComp.amount_to_compensate != null && (
          <Typography variant="body2">
            <strong>Montant à compenser :</strong>{' '}
            {parseFloat(victimComp.amount_to_compensate).toFixed(2)} {victimComp.currency || 'EUR'}
          </Typography>
        )}
        {victimComp.victim_user_id && (
          <Typography variant="body2">
            <strong>Victime (ID) :</strong>{' '}
            <Typography component="span" variant="caption" fontFamily="monospace">
              {victimComp.victim_user_id}
            </Typography>
          </Typography>
        )}
        {victimComp.compensation_method && (
          <Typography variant="body2">
            <strong>Méthode :</strong> {victimComp.compensation_method}
          </Typography>
        )}
        {victimComp.victim_has_stripe != null && (
          <Typography variant="body2">
            <strong>Compte Stripe victime :</strong>{' '}
            {victimComp.victim_has_stripe ? '✅ Oui' : '❌ Non'}
          </Typography>
        )}
        {victimComp.note && (
          <Alert severity={victimComp.status === 'requires_manual_action' ? 'error' : 'info'} sx={{ mt: 1 }}>
            {victimComp.note}
          </Alert>
        )}
        {victimComp.compensated_at && (
          <Typography variant="caption" color="text.secondary">
            Indemnisé le {formatDate(victimComp.compensated_at)}
          </Typography>
        )}
      </Stack>
    </Paper>
  );
};

// ─── Detail Dialog ──────────────────────────────────────────────────────────

const PenaltyDetailDialog = ({ penalty, open, onClose }) => {
  if (!penalty) return null;
  const statusCfg = STATUS_CHIP[penalty.status] || { color: 'default', label: penalty.status };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <PenaltyIcon color="warning" />
          <Typography variant="h6">Pénalité #{penalty.id?.substring(0, 8)}…</Typography>
        </Stack>
        <Chip label={statusCfg.label} color={statusCfg.color} size="small" />
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          {/* Left column */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>Informations générales</Typography>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Stack spacing={1}>
                <Box><Typography variant="caption" color="text.secondary">Utilisateur sanctionné</Typography>
                  <Typography variant="body2" fontWeight="bold">{penalty.user?.name || '-'}</Typography>
                  <Typography variant="caption">{penalty.user?.email}</Typography></Box>
                <Divider />
                <Box><Typography variant="caption" color="text.secondary">Type / Sévérité</Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Chip size="small" label={TYPE_LABEL[penalty.penalty_type] || penalty.penalty_type} />
                    {penalty.severity && <Chip size="small" label={penalty.severity} variant="outlined" />}
                  </Stack></Box>
                <Divider />
                <Box><Typography variant="caption" color="text.secondary">Montant</Typography>
                  <Typography variant="h6" color="error.main" fontWeight="bold">
                    {formatAmount(penalty.amount, penalty.currency)}
                  </Typography></Box>
                <Divider />
                <Box><Typography variant="caption" color="text.secondary">Source</Typography>
                  <Chip size="small" label={SOURCE_LABEL[penalty.source_type] || penalty.source_type} variant="outlined" />
                  {penalty.source_id && <Typography variant="caption" sx={{ ml: 1 }}>#{penalty.source_id?.substring(0, 8)}</Typography>}
                </Box>
                <Divider />
                <Box><Typography variant="caption" color="text.secondary">Raison</Typography>
                  <Typography variant="body2">{penalty.reason}</Typography></Box>
                {penalty.admin_notes && <>
                  <Divider />
                  <Box><Typography variant="caption" color="text.secondary">Notes admin</Typography>
                    <Typography variant="body2">{penalty.admin_notes}</Typography></Box>
                </>}
                <Divider />
                <Box><Typography variant="caption" color="text.secondary">Créé le</Typography>
                  <Typography variant="body2">{formatDate(penalty.created_at)}</Typography></Box>
                {penalty.applied_at && <Box><Typography variant="caption" color="text.secondary">Appliqué le</Typography>
                  <Typography variant="body2">{formatDate(penalty.applied_at)}</Typography></Box>}
              </Stack>
            </Paper>

            {/* Related links */}
            {(penalty.reservation_id || penalty.source_id) && (
              <>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>Liens associés</Typography>
                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Stack spacing={1}>
                    {penalty.reservation_id && (
                      <Button size="small" startIcon={<ReservationIcon />} variant="outlined"
                        onClick={() => window.open(`/admin/reservations?id=${penalty.reservation_id}`, '_blank')}>
                        Réservation #{penalty.reservation_id?.substring(0, 8)}
                      </Button>
                    )}
                    {penalty.source_type === 'dispute' && penalty.source_id && (
                      <Button size="small" startIcon={<DisputeIcon />} variant="outlined"
                        onClick={() => window.open(`/disputes/${penalty.source_id}`, '_blank')}>
                        Litige #{penalty.source_id?.substring(0, 8)}
                      </Button>
                    )}
                  </Stack>
                </Paper>
              </>
            )}

            {/* Execution metadata */}
            {penalty.execution_metadata && Object.keys(penalty.execution_metadata).length > 0 && (
              <>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>Compensation victime</Typography>
                <VictimCompensationCard victimComp={penalty.execution_metadata?.victim_compensation} />

                <Typography variant="subtitle2" color="text.secondary" gutterBottom>Métadonnées d'exécution</Typography>
                <Paper variant="outlined" sx={{ p: 1 }}>
                  <Box component="pre" sx={{ fontSize: '0.7rem', overflowX: 'auto', m: 0 }}>
                    {JSON.stringify(penalty.execution_metadata, null, 2)}
                  </Box>
                </Paper>
              </>
            )}
          </Grid>

          {/* Right column — events */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Historique des événements ({penalty.penaltyEvents?.length || 0})
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, maxHeight: 500, overflowY: 'auto' }}>
              <PenaltyTimeline events={penalty.penaltyEvents || []} />
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fermer</Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Create Penalty Dialog ──────────────────────────────────────────────────

const CreatePenaltyDialog = ({ open, onClose, onCreated }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    user_id: '', penalty_type: 'admin_decision', amount: '', reason: '',
    reservation_id: '', source_type: 'manual', source_id: '', severity: '', status: 'pending'
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    if (!form.user_id || !form.amount || !form.reason) {
      enqueueSnackbar('user_id, montant et raison sont obligatoires', { variant: 'warning' });
      return;
    }
    setLoading(true);
    try {
      const payload = {
        user_id: form.user_id, penalty_type: form.penalty_type,
        amount: parseFloat(form.amount), reason: form.reason,
        source_type: form.source_type, status: form.status
      };
      if (form.reservation_id) payload.reservation_id = form.reservation_id;
      if (form.source_id) payload.source_id = form.source_id;
      if (form.severity) payload.severity = form.severity;

      await penaltyService.adminCreate(payload);
      enqueueSnackbar('Pénalité créée avec succès', { variant: 'success' });
      onCreated();
      onClose();
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || err?.message || 'Erreur création', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AddIcon color="primary" /> Créer une pénalité manuelle
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField label="ID Utilisateur (UUID)*" value={form.user_id} onChange={e => set('user_id', e.target.value)} size="small" fullWidth />
          <FormControl size="small" fullWidth>
            <InputLabel>Type</InputLabel>
            <Select value={form.penalty_type} label="Type" onChange={e => set('penalty_type', e.target.value)}>
              {PENALTY_TYPES.map(t => <MenuItem key={t} value={t}>{TYPE_LABEL[t] || t}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label="Montant (EUR)*" type="number" value={form.amount} onChange={e => set('amount', e.target.value)} size="small" fullWidth inputProps={{ min: 0, step: 0.01 }} />
          <TextField label="Raison*" value={form.reason} onChange={e => set('reason', e.target.value)} size="small" fullWidth multiline rows={2} />
          <FormControl size="small" fullWidth>
            <InputLabel>Source</InputLabel>
            <Select value={form.source_type} label="Source" onChange={e => set('source_type', e.target.value)}>
              {SOURCE_TYPES.map(s => <MenuItem key={s} value={s}>{SOURCE_LABEL[s] || s}</MenuItem>)}
            </Select>
          </FormControl>
          {form.source_type !== 'manual' && (
            <TextField label="ID Source (UUID)" value={form.source_id} onChange={e => set('source_id', e.target.value)} size="small" fullWidth />
          )}
          <TextField label="ID Réservation (UUID, optionnel)" value={form.reservation_id} onChange={e => set('reservation_id', e.target.value)} size="small" fullWidth />
          <FormControl size="small" fullWidth>
            <InputLabel>Sévérité</InputLabel>
            <Select value={form.severity} label="Sévérité" onChange={e => set('severity', e.target.value)}>
              <MenuItem value="">—</MenuItem>
              <MenuItem value="minor">Mineure</MenuItem>
              <MenuItem value="moderate">Modérée</MenuItem>
              <MenuItem value="severe">Sévère</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel>Statut initial</InputLabel>
            <Select value={form.status} label="Statut initial" onChange={e => set('status', e.target.value)}>
              <MenuItem value="pending">En attente</MenuItem>
              <MenuItem value="applied">Appliqué</MenuItem>
              <MenuItem value="draft">Brouillon</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Annuler</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : <AddIcon />}>
          Créer
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Action Dialog ──────────────────────────────────────────────────────────

const ActionDialog = ({ open, onClose, title, icon, onConfirm, loading, children, confirmLabel = 'Confirmer', confirmColor = 'primary' }) => (
  <Dialog open={open} onClose={() => !loading && onClose()} maxWidth="sm" fullWidth>
    <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {icon} {title}
    </DialogTitle>
    <DialogContent>{children}</DialogContent>
    <DialogActions>
      <Button onClick={onClose} disabled={loading}>Annuler</Button>
      <Button onClick={onConfirm} variant="contained" color={confirmColor} disabled={loading}
        startIcon={loading ? <CircularProgress size={16} /> : null}>
        {confirmLabel}
      </Button>
    </DialogActions>
  </Dialog>
);

// ─── Main Page ───────────────────────────────────────────────────────────────

const PayoutDeductionsAdminPage = () => {
  const { enqueueSnackbar } = useSnackbar();

  // Tab
  const [activeTab, setActiveTab] = useState(0); // 0 = all, 1 = pending compensations

  // Stats
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  // List
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Filters
  const [status, setStatus] = useState('');
  const [penaltyType, setPenaltyType] = useState('');
  const [sourceType, setSourceType] = useState('');
  const [search, setSearch] = useState('');

  // UI state
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuPenalty, setMenuPenalty] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailPenalty, setDetailPenalty] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  // Action dialogs
  const [actionLoading, setActionLoading] = useState(false);
  const [waiveOpen, setWaiveOpen] = useState(false);
  const [waiveMessage, setWaiveMessage] = useState('');
  const [applyOpen, setApplyOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteMessage, setNoteMessage] = useState('');
  const [statusOpen, setStatusOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [compensateOpen, setCompensateOpen] = useState(false);
  const [compensateMethod, setCompensateMethod] = useState('manual_transfer');
  const [compensateRef, setCompensateRef] = useState('');
  const [compensateNotes, setCompensateNotes] = useState('');

  // ── Fetch ──────────────────────────────────────────────────────────────────

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const data = await penaltyService.getAdminStats();
      setStats(data);
    } catch (err) {
      console.error('stats error:', err);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await penaltyService.adminList({
        status: status || undefined,
        penalty_type: penaltyType || undefined,
        source_type: sourceType || undefined,
        search: search || undefined,
        victim_compensation_status: activeTab === 1 ? 'requires_manual_action' : undefined,
        page,
        limit: 20
      });
      setItems(result.data || []);
      setTotalPages(result.pagination?.totalPages || 1);
      setTotal(result.pagination?.total || 0);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [status, penaltyType, sourceType, search, page, activeTab]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchList(); }, [fetchList]);

  const handleRefresh = () => { fetchStats(); fetchList(); };

  // ── Menu ───────────────────────────────────────────────────────────────────

  const openMenu = (e, p) => { setMenuAnchorEl(e.currentTarget); setMenuPenalty(p); };
  const closeMenu = () => { setMenuAnchorEl(null); setMenuPenalty(null); };

  const openDetail = async (penaltyId) => {
    closeMenu();
    setDetailLoading(true);
    setDetailOpen(true);
    try {
      const p = await penaltyService.get(penaltyId);
      setDetailPenalty(p.data || p);
    } catch (err) {
      enqueueSnackbar('Erreur chargement détails', { variant: 'error' });
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  // ── Actions ────────────────────────────────────────────────────────────────

  const handleWaive = async () => {
    if (!menuPenalty) return;
    setActionLoading(true);
    try {
      await penaltyService.adminUpdateStatus(menuPenalty.id, { status: 'waived', message: waiveMessage, admin_notes: waiveMessage });
      enqueueSnackbar('Pénalité annulée (waive)', { variant: 'success' });
      setWaiveOpen(false); setWaiveMessage(''); closeMenu();
      fetchList(); fetchStats();
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || err?.message || 'Erreur', { variant: 'error' });
    } finally { setActionLoading(false); }
  };

  const handleApply = async () => {
    if (!menuPenalty) return;
    setActionLoading(true);
    try {
      await penaltyService.adminApplyMonetary(menuPenalty.id);
      enqueueSnackbar('Collecte monétaire déclenchée', { variant: 'success' });
      setApplyOpen(false); closeMenu();
      fetchList(); fetchStats();
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || err?.message || 'Erreur', { variant: 'error' });
    } finally { setActionLoading(false); }
  };

  const handleAddNote = async () => {
    if (!menuPenalty || !noteMessage) return;
    setActionLoading(true);
    try {
      await penaltyService.adminAddEvent(menuPenalty.id, { event_type: 'note', message: noteMessage });
      enqueueSnackbar('Note ajoutée', { variant: 'success' });
      setNoteOpen(false); setNoteMessage(''); closeMenu();
      fetchList();
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || err?.message || 'Erreur', { variant: 'error' });
    } finally { setActionLoading(false); }
  };

  const handleStatusChange = async () => {
    if (!menuPenalty || !newStatus) return;
    setActionLoading(true);
    try {
      await penaltyService.adminUpdateStatus(menuPenalty.id, { status: newStatus, message: statusMessage });
      enqueueSnackbar('Statut mis à jour', { variant: 'success' });
      setStatusOpen(false); setNewStatus(''); setStatusMessage(''); closeMenu();
      fetchList(); fetchStats();
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || err?.message || 'Erreur', { variant: 'error' });
    } finally { setActionLoading(false); }
  };

  const handleCompensate = async () => {
    if (!menuPenalty) return;
    setActionLoading(true);
    try {
      await penaltyService.adminMarkCompensated(menuPenalty.id, {
        compensation_method: compensateMethod,
        compensation_reference: compensateRef,
        notes: compensateNotes
      });
      enqueueSnackbar('Victime marquée comme indemnisée', { variant: 'success' });
      setCompensateOpen(false); closeMenu();
      fetchList();
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || err?.message || 'Erreur', { variant: 'error' });
    } finally { setActionLoading(false); }
  };

  // ── Stats helpers ─────────────────────────────────────────────────────────

  const sc = (key) => stats?.by_status?.[key]?.count || 0;
  const sa = (key) => parseFloat(stats?.by_status?.[key]?.total_amount || 0).toFixed(2);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PenaltyIcon color="error" fontSize="large" />
            Gestion des Pénalités
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Administration complète des pénalités — {total} pénalité(s) au total
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
            Nouvelle pénalité
          </Button>
          <Tooltip title="Rafraîchir">
            <IconButton onClick={handleRefresh} color="primary"><RefreshIcon /></IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* Alert: pending victim compensations */}
      {!statsLoading && stats?.requires_manual_compensation > 0 && (
        <Alert
          severity="error"
          icon={<UrgentIcon />}
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={() => { setActiveTab(1); setPage(1); }}>
              Voir ({stats.requires_manual_compensation})
            </Button>
          }
        >
          <AlertTitle>Compensations victimes en attente</AlertTitle>
          <strong>{stats.requires_manual_compensation}</strong> pénalité(s) ont été collectées auprès de l'auteur
          mais le virement vers la victime a échoué et nécessite une action manuelle.
        </Alert>
      )}

      {/* Tabs */}
      <Paper elevation={1} sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => { setActiveTab(v); setPage(1); }}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
        >
          <Tab label="Toutes les pénalités" />
          <Tab
            label={
              <Badge
                badgeContent={stats?.requires_manual_compensation || 0}
                color="error"
                max={99}
              >
                <Box sx={{ pr: stats?.requires_manual_compensation > 0 ? 2 : 0 }}>
                  🚨 Compensations en attente
                </Box>
              </Badge>
            }
          />
        </Tabs>
      </Paper>

      {/* Stats cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard title="Total" value={statsLoading ? null : stats?.grand_count}
            color="primary" icon={<PenaltyIcon />}
            sub={statsLoading ? null : `${parseFloat(stats?.grand_total || 0).toFixed(2)} €`} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard title="En attente" value={statsLoading ? null : sc('pending')}
            color="warning" icon={<PendingIcon />} sub={statsLoading ? null : `${sa('pending')} €`} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard title="Recouvrement" value={statsLoading ? null : sc('pending_collection')}
            color="warning" icon={<DeductionIcon />} sub={statsLoading ? null : `${sa('pending_collection')} €`} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard title="Appliqués" value={statsLoading ? null : sc('applied')}
            color="success" icon={<CheckIcon />} sub={statsLoading ? null : `${sa('applied')} €`} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard title="Collectés" value={statsLoading ? null : sc('collected')}
            color="success" icon={<CollectedIcon />} sub={statsLoading ? null : `${sa('collected')} €`} />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard title="🚨 Compensation en attente" value={statsLoading ? null : stats?.requires_manual_compensation}
            color={stats?.requires_manual_compensation > 0 ? 'error' : 'default'} icon={<VictimIcon />} />
        </Grid>
      </Grid>

      {/* Secondary stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3} md={1.5}>
          <StatCard title="Contestés" value={statsLoading ? null : sc('disputed')} color="error" icon={<DisputeIcon />} />
        </Grid>
        <Grid item xs={6} sm={3} md={1.5}>
          <StatCard title="Échecs" value={statsLoading ? null : sc('failed')} color="error" icon={<ErrorIcon />} />
        </Grid>
        <Grid item xs={6} sm={3} md={1.5}>
          <StatCard title="Annulés" value={statsLoading ? null : sc('waived') + sc('cancelled')} color="inherit" icon={<CancelIcon />} />
        </Grid>
        <Grid item xs={6} sm={3} md={1.5}>
          <StatCard title="Planifiés" value={statsLoading ? null : sc('scheduled')} color="info" icon={<ScheduledIcon />} />
        </Grid>
        <Grid item xs={6} sm={3} md={1.5}>
          <StatCard title="30 derniers j." value={statsLoading ? null : stats?.recent_30d} color="info" icon={<EventIcon />} />
        </Grid>
        {/* Type breakdown */}
        {!statsLoading && stats?.by_type?.slice(0, 6).map(({ type, count }) => (
          <Grid item xs={6} sm={3} md={1.5} key={type}>
            <StatCard title={TYPE_LABEL[type] || type} value={count} color="inherit" icon={<PenaltyIcon />} />
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Statut</InputLabel>
            <Select value={status} label="Statut" onChange={e => { setStatus(e.target.value); setPage(1); }}>
              <MenuItem value="">Tous</MenuItem>
              {PENALTY_STATUSES.map(s => <MenuItem key={s} value={s}>{STATUS_CHIP[s]?.label || s}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Type</InputLabel>
            <Select value={penaltyType} label="Type" onChange={e => { setPenaltyType(e.target.value); setPage(1); }}>
              <MenuItem value="">Tous</MenuItem>
              {PENALTY_TYPES.map(t => <MenuItem key={t} value={t}>{TYPE_LABEL[t] || t}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Source</InputLabel>
            <Select value={sourceType} label="Source" onChange={e => { setSourceType(e.target.value); setPage(1); }}>
              <MenuItem value="">Toutes</MenuItem>
              {SOURCE_TYPES.map(s => <MenuItem key={s} value={s}>{SOURCE_LABEL[s] || s}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField
            size="small" placeholder="Rechercher (raison, ID…)" value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} /> }}
            sx={{ minWidth: 260 }}
          />
          {(status || penaltyType || sourceType || search) && (
            <Button size="small" onClick={() => { setStatus(''); setPenaltyType(''); setSourceType(''); setSearch(''); setPage(1); }}>
              Réinitialiser
            </Button>
          )}
        </Stack>
      </Paper>

      {/* Table */}
      <Paper elevation={2}>
        {loading ? (
          <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>
        ) : error ? (
          <Box sx={{ p: 3 }}><Alert severity="error">{error}</Alert></Box>
        ) : items.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">Aucune pénalité trouvée.</Typography>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'error.lighter' }}>
                    <TableCell>ID</TableCell>
                    <TableCell>Utilisateur</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Montant</TableCell>
                    <TableCell>Statut</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell>Réservation</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map(p => {
                    const sc = STATUS_CHIP[p.status] || { color: 'default', label: p.status };
                    const victimComp = p.execution_metadata?.victim_compensation;
                    const needsCompensation = victimComp?.status === 'requires_manual_action';
                    return (
                      <TableRow
                        key={p.id}
                        hover
                        sx={needsCompensation ? { bgcolor: 'error.lighter' } : undefined}
                      >
                        <TableCell>
                          <Typography variant="caption" fontFamily="monospace">
                            #{p.id?.substring(0, 8)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{p.user?.name || '—'}</Typography>
                          <Typography variant="caption" color="text.secondary">{p.user?.email}</Typography>
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0.5}>
                            <Chip size="small" label={TYPE_LABEL[p.penalty_type] || p.penalty_type} sx={{ fontSize: '0.7rem' }} />
                            {p.severity && <Typography variant="caption" color="text.secondary">{p.severity}</Typography>}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography fontWeight="bold" color="error.main">
                            {formatAmount(p.amount, p.currency)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0.5}>
                            <Chip size="small" label={sc.label} color={sc.color} />
                            {needsCompensation && (
                              <Tooltip title={`Compensation victime requise : ${victimComp?.note || ''}`}>
                                <Chip size="small" icon={<UrgentIcon />} label="Victime non indemnisée" color="error" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" variant="outlined" label={SOURCE_LABEL[p.source_type] || p.source_type} sx={{ fontSize: '0.7rem' }} />
                        </TableCell>
                        <TableCell>
                          {p.reservation_id
                            ? <Typography variant="caption" fontFamily="monospace">#{p.reservation_id?.substring(0, 8)}</Typography>
                            : <Typography variant="caption" color="text.disabled">—</Typography>}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">{formatDate(p.created_at)}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            <Tooltip title="Voir les détails">
                              <IconButton size="small" color="primary" onClick={() => openDetail(p.id)}>
                                <OpenInNewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <IconButton size="small" onClick={e => openMenu(e, p)}>
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {totalPages > 1 && (
              <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
                <Pagination count={totalPages} page={page} onChange={(_, p) => setPage(p)} color="primary" />
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* Actions Menu */}
      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={closeMenu}>
        <MenuItem onClick={() => { openDetail(menuPenalty?.id); }}>
          <ListItemIcon><OpenInNewIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Voir les détails</ListItemText>
        </MenuItem>
        <Divider />
        {menuPenalty && ['pending', 'pending_collection', 'scheduled'].includes(menuPenalty.status) && (
          <MenuItem onClick={() => { setApplyOpen(true); }}>
            <ListItemIcon><ApplyIcon fontSize="small" color="success" /></ListItemIcon>
            <ListItemText>Déclencher la collecte</ListItemText>
          </MenuItem>
        )}
        {menuPenalty && ['pending', 'applied', 'pending_collection', 'failed', 'scheduled'].includes(menuPenalty.status) && (
          <MenuItem onClick={() => { setWaiveOpen(true); }}>
            <ListItemIcon><WaiveIcon fontSize="small" color="warning" /></ListItemIcon>
            <ListItemText>Annuler (waive)</ListItemText>
          </MenuItem>
        )}
        {menuPenalty && menuPenalty.status === 'applied' && (
          <MenuItem onClick={() => { setCompensateOpen(true); }}>
            <ListItemIcon><CollectedIcon fontSize="small" color="info" /></ListItemIcon>
            <ListItemText>Marquer victime indemnisée</ListItemText>
          </MenuItem>
        )}
        {menuPenalty && menuPenalty.status === 'collected' &&
          menuPenalty.execution_metadata?.victim_compensation?.status === 'requires_manual_action' && (
          <MenuItem onClick={() => { setCompensateOpen(true); }}>
            <ListItemIcon><CompensateIcon fontSize="small" color="error" /></ListItemIcon>
            <ListItemText sx={{ color: 'error.main' }}>🚨 Indemniser la victime (en attente)</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={() => { setStatusOpen(true); setNewStatus(menuPenalty?.status || ''); }}>
          <ListItemIcon><FilterIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Changer le statut</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { setNoteOpen(true); }}>
          <ListItemIcon><NoteIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Ajouter une note</ListItemText>
        </MenuItem>
        {menuPenalty?.source_type === 'dispute' && menuPenalty?.source_id && (
          <>
            <Divider />
            <MenuItem onClick={() => { window.open(`/disputes/${menuPenalty.source_id}`, '_blank'); closeMenu(); }}>
              <ListItemIcon><DisputeIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Ouvrir le litige</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Detail Dialog */}
      {detailOpen && (
        detailLoading
          ? <Dialog open fullWidth maxWidth="md"><DialogContent sx={{ p: 6, display: 'flex', justifyContent: 'center' }}><CircularProgress /></DialogContent></Dialog>
          : <PenaltyDetailDialog penalty={detailPenalty} open={detailOpen} onClose={() => { setDetailOpen(false); setDetailPenalty(null); }} />
      )}

      {/* Create Dialog */}
      <CreatePenaltyDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={() => { fetchList(); fetchStats(); }} />

      {/* Waive Dialog */}
      <ActionDialog open={waiveOpen} onClose={() => setWaiveOpen(false)} title="Annuler la pénalité"
        icon={<WaiveIcon color="warning" />} onConfirm={handleWaive} loading={actionLoading}
        confirmLabel="Confirmer l'annulation" confirmColor="warning">
        <Typography variant="body2" sx={{ mb: 2 }}>
          Cette pénalité sera marquée comme <strong>waived</strong> (annulée). Précisez la raison pour l'audit.
        </Typography>
        <TextField fullWidth multiline rows={3} label="Raison (optionnel)" value={waiveMessage}
          onChange={e => setWaiveMessage(e.target.value)} size="small" />
      </ActionDialog>

      {/* Apply Dialog */}
      <ActionDialog open={applyOpen} onClose={() => setApplyOpen(false)} title="Déclencher la collecte monétaire"
        icon={<ApplyIcon color="success" />} onConfirm={handleApply} loading={actionLoading}
        confirmLabel="Lancer la collecte" confirmColor="success">
        <Alert severity="warning" sx={{ mb: 2 }}>
          Cette action va tenter de collecter le montant via dépôt Stripe ou déduction de payout.
          L'opération est irréversible.
        </Alert>
        {menuPenalty && (
          <Typography variant="body2">
            Pénalité #{menuPenalty.id?.substring(0, 8)} — <strong>{formatAmount(menuPenalty.amount, menuPenalty.currency)}</strong>
          </Typography>
        )}
      </ActionDialog>

      {/* Add Note Dialog */}
      <ActionDialog open={noteOpen} onClose={() => setNoteOpen(false)} title="Ajouter une note"
        icon={<NoteIcon />} onConfirm={handleAddNote} loading={actionLoading} confirmLabel="Ajouter">
        <TextField fullWidth multiline rows={4} label="Note*" value={noteMessage}
          onChange={e => setNoteMessage(e.target.value)} size="small" sx={{ mt: 1 }} />
      </ActionDialog>

      {/* Status Change Dialog */}
      <ActionDialog open={statusOpen} onClose={() => setStatusOpen(false)} title="Changer le statut"
        icon={<FilterIcon />} onConfirm={handleStatusChange} loading={actionLoading} confirmLabel="Mettre à jour">
        <Stack spacing={2} sx={{ pt: 1 }}>
          <FormControl size="small" fullWidth>
            <InputLabel>Nouveau statut</InputLabel>
            <Select value={newStatus} label="Nouveau statut" onChange={e => setNewStatus(e.target.value)}>
              {PENALTY_STATUSES.map(s => <MenuItem key={s} value={s}>{STATUS_CHIP[s]?.label || s}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField fullWidth multiline rows={2} label="Message / raison (optionnel)" value={statusMessage}
            onChange={e => setStatusMessage(e.target.value)} size="small" />
        </Stack>
      </ActionDialog>

      {/* Compensate Dialog */}
      <ActionDialog open={compensateOpen} onClose={() => setCompensateOpen(false)}
        title={
          menuPenalty?.execution_metadata?.victim_compensation?.status === 'requires_manual_action'
            ? '🚨 Indemnisation manuelle requise'
            : 'Marquer la victime comme indemnisée'
        }
        icon={<CompensateIcon color={menuPenalty?.execution_metadata?.victim_compensation?.status === 'requires_manual_action' ? 'error' : 'info'} />}
        onConfirm={handleCompensate} loading={actionLoading}
        confirmLabel="Confirmer l'indemnisation"
        confirmColor={menuPenalty?.execution_metadata?.victim_compensation?.status === 'requires_manual_action' ? 'error' : 'info'}>
        {menuPenalty?.execution_metadata?.victim_compensation?.status === 'requires_manual_action' && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <AlertTitle>Action manuelle requise</AlertTitle>
            {menuPenalty.execution_metadata.victim_compensation.note || 'Le virement automatique a échoué.'}
            {menuPenalty.execution_metadata.victim_compensation.amount_to_compensate != null && (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                <strong>Montant à transférer manuellement :</strong>{' '}
                {parseFloat(menuPenalty.execution_metadata.victim_compensation.amount_to_compensate).toFixed(2)}{' '}
                {menuPenalty.execution_metadata.victim_compensation.currency || 'EUR'}
              </Typography>
            )}
          </Alert>
        )}
        <Stack spacing={2} sx={{ pt: 1 }}>
          <FormControl size="small" fullWidth>
            <InputLabel>Méthode</InputLabel>
            <Select value={compensateMethod} label="Méthode" onChange={e => setCompensateMethod(e.target.value)}>
              <MenuItem value="manual_transfer">Virement manuel</MenuItem>
              <MenuItem value="bank_transfer">Virement bancaire</MenuItem>
              <MenuItem value="stripe_payout">Stripe payout</MenuItem>
              <MenuItem value="cash">Espèces</MenuItem>
              <MenuItem value="other">Autre</MenuItem>
            </Select>
          </FormControl>
          <TextField fullWidth label="Référence (optionnel)" value={compensateRef}
            onChange={e => setCompensateRef(e.target.value)} size="small" />
          <TextField fullWidth multiline rows={2} label="Notes (optionnel)" value={compensateNotes}
            onChange={e => setCompensateNotes(e.target.value)} size="small" />
        </Stack>
      </ActionDialog>
    </Box>
  );
};

export default PayoutDeductionsAdminPage;

